import { z } from "zod/mini";
import { createTypedStorage, defineKey, type TypedStorage } from "./local.ts";
import type { KeyValueStore } from "../platform.ts";

/**
 * FR-015-016: at most 50 drafts, each at most 7 days since its last edit, evicted oldest
 * first and silently on the next write, and every draft removed on explicit sign-out, on
 * sign-out-everywhere and when the re-authentication dialog is dismissed without a
 * successful sign-in.
 */
export const DRAFT_MAX_COUNT = 50;
export const DRAFT_MAX_AGE_MS = 7 * 24 * 3600_000;

export const draftSchema = z.object({
  kind: z.enum(["comment", "description", "notes"]),
  body: z.string(),
  editedAt: z.int(),
});

export type Draft = z.infer<typeof draftSchema>;

export const draftKey = defineKey({ name: "draft", version: 1, schema: draftSchema });

export interface Drafts {
  get(itemId: string, kind: Draft["kind"]): Draft | null;
  /** Save a draft; the bounds are enforced on this write, oldest first, silently. */
  set(itemId: string, kind: Draft["kind"], body: string, now?: number): void;
  remove(itemId: string, kind: Draft["kind"]): void;
  /** Every draft, on sign-out, sign-out-everywhere and a dismissed re-authentication. */
  clearAll(): void;
  count(): number;
}

function scope(itemId: string, kind: Draft["kind"]): string {
  return `${kind}:${itemId}`;
}

export function createDrafts(store: KeyValueStore, storage: TypedStorage = createTypedStorage(store), clock: () => number = Date.now): Drafts {
  const all = () =>
    storage
      .scopesOf(draftKey)
      .map((s) => ({ scope: s, draft: storage.get(draftKey, s) }))
      .filter((entry): entry is { scope: string; draft: Draft } => entry.draft !== null);
  const evict = (now: number) => {
    const live = all().filter(({ scope: s, draft }) => {
      if (now - draft.editedAt > DRAFT_MAX_AGE_MS) {
        storage.remove(draftKey, s);
        return false;
      }
      return true;
    });
    live.sort((a, b) => a.draft.editedAt - b.draft.editedAt);
    while (live.length > DRAFT_MAX_COUNT) {
      const oldest = live.shift();
      if (oldest) storage.remove(draftKey, oldest.scope);
    }
  };
  return {
    get(itemId, kind) {
      const draft = storage.get(draftKey, scope(itemId, kind));
      if (draft === null) return null;
      if (clock() - draft.editedAt > DRAFT_MAX_AGE_MS) {
        storage.remove(draftKey, scope(itemId, kind));
        return null;
      }
      return draft;
    },
    set(itemId, kind, body, now = clock()) {
      storage.set(draftKey, { kind, body, editedAt: now }, scope(itemId, kind));
      evict(now);
    },
    remove(itemId, kind) {
      storage.remove(draftKey, scope(itemId, kind));
    },
    clearAll() {
      for (const s of storage.scopesOf(draftKey)) storage.remove(draftKey, s);
    },
    count: () => all().length,
  };
}
